import { Link } from 'react-router-dom';
import {
  LegalPage,
  Todo, Section, P, Table, Th, Td, Note, Ul, Ol, B, Basis,
  useLegalDocumentHead,
} from './components';

/**
 * 서비스 이용약관.
 *
 * 처리방침(고지)과 달리 **약관**이므로 이용자 동의가 필요하다.
 * 앱 가입 화면의 "회원 약관 및 동의사항" 체크박스가 동의하는 대상이다.
 *
 * 이 문서가 담당하는 것:
 * - 하트(유상 재화)의 구매·사용·환불·소멸 근거
 * - 금지행위와 이용 제한 → 재가입 방지(제재)의 근거. privacy-policy-draft 5.1과 연동
 * - 이용자 간 만남에서 발생하는 문제에 대한 회사 책임 범위
 *
 * 제12조(청약철회 및 환불)는 전자상거래법 적용 영역. 2026-08-25 리서치로 조문을 확정했다:
 * - 전상법 §17① 청약철회 기간 7일, §17②5호 단서(가분적 디지털콘텐츠는 미제공분 철회 가능)
 * - Google Play는 개발사가 Play Console/orders.refund API로 부분 환불 실행 가능,
 *   Apple은 merchant of record가 Apple이라 개발사에 환불 실행 수단이 없다(거래 단위 전액만)
 * - 법정 청약철회(7일)에는 공제 불가. 게임 표준약관의 10% 공제는 철회 기간 경과 후
 *   임의 해지 환불에 적용되는 별개 제도 — 혼용하면 §35(불리한 특약 무효) 리스크
 * 잔여 법률 검토 2건은 제12조 하단 Todo 참조. 조문은 소비자에게 유리한 방향으로
 * 작성했으므로 검토 결과에 따라 완화는 가능해도 위반은 아니다.
 */
export default function ServiceTerms() {
  useLegalDocumentHead('서비스 이용약관 | Cupid');

  return (
    <LegalPage title="서비스 이용약관" subtitle="시행일자: 2026년 10월 1일">

        {/*
          ⚠️ 게시 전 남은 작업
          2. useLegalDocumentHead 두 번째 인자를 false로 바꿔 색인 허용
          3. 법률 검토 — 특히 제12·13조(유료서비스·청약철회), 제16조(이용제한), 제18조(면책)
          4. 제16조 이용제한은 코드 미구현(Status enum이 A/D뿐) — 제재 기능 구현 필요
             → cupid-application-server/docs/2026_08_17_privacy-policy-draft.md 5.1
        */}

        <Section no={1} title="목적">
          <P>
            이 약관은 Cupid(이하 "회사")가 제공하는 그룹 매칭 데이팅 서비스 "Cupid"(이하 "서비스")의 이용과 관련하여 회사와
            회원 간의 권리, 의무 및 책임사항, 서비스 이용조건 및 절차 등 기본적인 사항을 규정함을 목적으로 합니다.
          </P>
        </Section>

        <Section no={2} title="정의">
          <P>이 약관에서 사용하는 용어의 정의는 다음과 같습니다.</P>
          <Table>
            <thead><tr><Th>용어</Th><Th>정의</Th></tr></thead>
            <tbody>
              <tr><Td>서비스</Td><Td>회사가 제공하는 Cupid 모바일 애플리케이션 및 이에 부수하는 일체의 서비스</Td></tr>
              <tr><Td>회원</Td><Td>이 약관에 동의하고 본인확인을 거쳐 회사와 이용계약을 체결한 자</Td></tr>
              <tr><Td>그룹</Td><Td>회원이 친구를 초대하여 구성하는 2인 이상의 매칭 단위</Td></tr>
              <tr><Td>매칭</Td><Td>위치·연령·성별·인원수 조건에 따라 상대 그룹을 제안하고, 양 그룹 구성원의 수락으로 성사되는 절차</Td></tr>
              <tr><Td>하트</Td><Td>매칭 성사 시 사용되는 서비스 내 유상 재화. 회사가 지급하거나 회원이 인앱결제로 구매한다</Td></tr>
            </tbody>
          </Table>
        </Section>

        <Section no={3} title="약관의 게시와 개정">
          <P>① 회사는 이 약관을 서비스 초기화면 또는 회사 누리집에 게시합니다.</P>
          <P>
            ② 회사는 관련 법령을 위배하지 않는 범위에서 이 약관을 개정할 수 있으며, 개정 시에는 적용일자와 개정사유를 명시하여
            적용일자 <B>7일 전</B>부터 공지합니다. 다만 회원에게 불리한 내용으로 개정하는 경우에는 <B>30일 전</B>부터
            공지하고, 앱 내 알림 등으로 개별 통지합니다.
          </P>
          <P>
            ③ 회원이 개정 약관의 적용일자까지 거부의사를 표시하지 않으면 개정 약관에 동의한 것으로 봅니다. 개정 약관에 동의하지
            않는 회원은 이용계약을 해지(회원 탈퇴)할 수 있습니다.
          </P>
        </Section>

        <Section no={4} title="이용계약의 체결">
          <P>
            ① 이용계약은 회원이 되려는 자가 이 약관에 동의하고 본인확인 절차를 완료한 후, 회사가 이를 승낙함으로써 체결됩니다.
          </P>
          <P>
            ② 서비스는 <B>만 19세 이상</B>만 이용할 수 있습니다. 회사는 본인확인 기관을 통해 확인된 생년월일을 기준으로 연령을
            확인합니다.
          </P>
          <P>③ 회사는 다음의 경우 이용 신청을 승낙하지 않거나 사후에 이용계약을 해지할 수 있습니다.</P>
          <Ol>
            <li>만 19세 미만인 경우</li>
            <li>타인의 명의를 이용하거나 허위 정보를 기재한 경우</li>
            <li>이 약관에 따라 이용이 제한되었던 이력이 있는 경우</li>
            <li>기타 회사가 정한 이용 신청 요건을 충족하지 못한 경우</li>
          </Ol>
          <P>
            ④ 서비스는 <B>1인당 1개의 계정</B>만 허용하며, 하나의 계정은 <B>단일 기기</B>에서만 로그인할 수 있습니다. 다른
            기기에서 로그인하는 경우 기존 기기의 로그인은 해제됩니다.
          </P>
        </Section>

        <Section no={5} title="회원정보의 관리">
          <P>
            ① 회원은 프로필 정보를 사실에 부합하도록 등록·유지해야 하며, 등록 정보가 변경된 경우 앱 내에서 수정해야 합니다.
          </P>
          <P>
            ② 회원은 자신의 계정을 제3자에게 양도·대여할 수 없습니다. 계정 관리 소홀로 발생한 손해에 대한 책임은 회원에게
            있습니다.
          </P>
          <P>③ 개인정보의 처리에 관한 사항은 <Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link>에서 정합니다.</P>
        </Section>

        <Section no={6} title="서비스의 제공">
          <P>① 회사는 다음의 서비스를 제공합니다.</P>
          <Ol>
            <li>친구 검색·요청·관리</li>
            <li>그룹 생성 및 친구 초대</li>
            <li>위치 기반 그룹 매칭 — 세부 사항은 <Link to="/location-terms" className="text-red-400 hover:text-red-300">위치기반서비스 이용약관</Link>에서 정합니다</li>
            <li>매칭 성사 시 그룹 채팅, 1:1 채팅</li>
            <li>하트 구매 및 이용</li>
          </Ol>
          <P>
            ② 서비스는 연중무휴 1일 24시간 제공을 원칙으로 합니다. 다만 회사는 시스템 점검·교체, 설비 장애, 통신 중단, 천재지변
            등의 사유로 서비스의 전부 또는 일부를 제한·중지할 수 있으며, 이 경우 사전에 공지합니다. 사전 공지가 불가능한
            긴급한 사유가 있는 경우에는 사후에 지체 없이 공지합니다.
          </P>
          <P>
            ③ 회사는 서비스의 내용을 변경할 수 있으며, 중요한 변경의 경우 사전에 공지합니다.
          </P>
        </Section>

        <Section no={7} title="그룹 및 매칭">
          <P>① 그룹은 2인 이상으로 구성되며, 매칭은 동일한 인원수의 이성 그룹을 대상으로 이루어집니다.</P>
          <P>
            ② 매칭 제안에 대한 수락·거절은 그룹 구성원의 투표로 결정됩니다. 양 그룹이 모두 수락하면 매칭이 성사되고 그룹
            채팅방이 생성됩니다.
          </P>
          <P>
            ③ 회사는 매칭 조건에 부합하는 상대 그룹을 검색하여 제안하나, <B>매칭의 성사 자체를 보장하지 않습니다.</B> 주변에
            조건에 맞는 그룹이 없는 경우 제안이 이루어지지 않을 수 있습니다.
          </P>
          <P>
            ④ 매칭 진행 중 그룹 구성원이 이탈하여 인원이 최소 인원 미만이 되면 해당 그룹의 매칭은 자동으로 중단됩니다.
          </P>
        </Section>

        <Section no={8} title="회원의 의무 및 금지행위">
          <P>① 회원은 관련 법령, 이 약관의 규정, 이용안내 및 공지사항을 준수해야 합니다.</P>
          <P>② 회원은 다음 행위를 해서는 안 됩니다.</P>
          <Ol>
            <li>타인의 명의·정보를 도용하거나 허위 정보를 등록하는 행위</li>
            <li>타인의 사진을 자신의 프로필 사진으로 등록하는 행위</li>
            <li>성적 수치심을 유발하는 표현물을 게시·전송하는 행위</li>
            <li>다른 회원을 협박·비방·괴롭히거나 혐오 표현을 사용하는 행위</li>
            <li>성매매·유흥업소 알선 등 법령이 금지하는 행위를 목적으로 서비스를 이용하는 행위</li>
            <li>영리·홍보·종교 포교 등 서비스 취지에 부합하지 않는 목적으로 서비스를 이용하는 행위</li>
            <li>금전 요구, 투자 유인, 대출 권유 등 재산상 이익을 목적으로 접근하는 행위</li>
            <li>외부 메신저·사이트로 유인하는 행위</li>
            <li>서비스의 정상적인 운영을 방해하는 행위(자동화 프로그램 이용, 위치정보 조작, 비정상적 접근 등)</li>
            <li>회사 또는 제3자의 지식재산권을 침해하는 행위</li>
            <li>회원가입 및 탈퇴를 반복하여 신규 가입 혜택을 부당하게 취득하는 행위</li>
            <li>기타 관련 법령에 위반되거나 선량한 풍속 및 사회질서에 반하는 행위</li>
          </Ol>
          <Note>
            <B>안전한 만남을 위한 안내.</B> 서비스는 회원 간의 만남을 매개할 뿐이며, 실제 만남에서 발생하는 사항은 회원 본인의
            판단과 책임에 따릅니다. 금전을 요구하거나 외부 사이트로 유인하는 상대는 즉시 신고해 주시기 바랍니다.
          </Note>
        </Section>

        <Section no={9} title="신고 및 이용 제한">
          <P>
            ① 회원은 다른 회원의 제8조 위반 행위를 회사에 신고할 수 있습니다. 회사는 신고를 접수한 후 확인 절차를 거쳐 필요한
            조치를 취합니다.
          </P>
          <P>② 회사는 회원이 제8조를 위반한 경우 다음의 조치를 단계적으로 또는 즉시 취할 수 있습니다.</P>
          <Ol>
            <li>경고</li>
            <li>게시물·프로필 삭제 또는 노출 제한</li>
            <li>서비스 이용의 일시 정지</li>
            <li>이용계약의 해지(영구 이용 정지)</li>
          </Ol>
          <P>
            ③ 위반의 정도가 중대하거나 다른 회원의 안전에 위해가 될 우려가 있는 경우, 회사는 사전 통지 없이 즉시 이용을 정지할
            수 있습니다. 이 경우 사후에 그 사유를 통지합니다.
          </P>
          <P>
            ④ 회사는 이용 제한 조치에 대해 회원이 이의를 제기할 수 있는 절차를 제공합니다. 회원은 제19조의 연락처로 이의를
            제기할 수 있으며, 회사는 이의가 정당하다고 인정되는 경우 조치를 해제합니다.
          </P>
          <P>
            ⑤ <B>제2항 제4호에 따라 이용계약이 해지된 회원은 재가입할 수 없습니다.</B> 회사는 이를 확인하기 위해 필요한 최소한의
            정보를 <Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link>이 정한 기간 동안
            보관합니다.
          </P>
          <P>⑥ 이용 제한으로 인해 사용하지 못한 하트에 대해서는 환불하지 않습니다.</P>
        </Section>

        <Section no={10} title="게시물의 관리">
          <P>
            ① 회원이 서비스에 등록한 프로필 사진, 채팅 메시지 등(이하 "게시물")에 대한 권리와 책임은 게시한 회원에게 있습니다.
          </P>
          <P>
            ② 회사는 회원이 등록한 프로필 사진이 부적절한 내용을 포함하는지 자동으로 검사하며, 부적절하다고 판단되는 경우 등록을
            거부할 수 있습니다.
          </P>
          <P>
            ③ 회사는 게시물이 관련 법령 또는 제8조에 위반된다고 판단되는 경우 이를 삭제하거나 노출을 제한할 수 있습니다.
          </P>
          <P>
            ④ 회원 탈퇴 시 회원의 게시물은 삭제됩니다. 다만 채팅 메시지와 같이 다른 회원의 이용 기록에 포함되는 게시물은 삭제되지
            않으며, 발신자 표시가 "탈퇴한 사용자"로 대체됩니다.
          </P>
        </Section>

        <Section no={11} title="하트의 지급 및 사용">
          <P>① 하트는 매칭 성사 시 사용되는 서비스 내 유상 재화입니다.</P>
          <Table>
            <thead><tr><Th>구분</Th><Th>내용</Th></tr></thead>
            <tbody>
              <tr><Td>지급</Td><Td>회원가입 시 3개가 무상 지급됩니다.</Td></tr>
              <tr><Td>구매</Td><Td>앱 내 결제(App Store 또는 Google Play 인앱결제)를 통해 구매할 수 있습니다.</Td></tr>
              <tr><Td>사용</Td><Td><B>매칭이 성사되는 시점</B>에 참여 구성원별로 각 1개가 차감됩니다.</Td></tr>
              <tr><Td>미차감</Td><Td>그룹 생성·참여 및 매칭 진행 단계에서는 보유 여부만 확인하며 차감되지 않습니다.</Td></tr>
              <tr><Td>차감 복구</Td><Td>차감 후 채팅방 생성 등 후속 절차가 실패한 경우 차감된 하트를 복구합니다.</Td></tr>
            </tbody>
          </Table>
          <P>
            ② 하트는 현금으로 환전할 수 없으며, 다른 회원에게 양도하거나 대여할 수 없습니다.
          </P>
          <P>
            ③ <B>회원 탈퇴 시 보유 중인 하트는 소멸되며 환불되지 않습니다.</B> 잔여 하트가 있는 회원은 사용 후 탈퇴하시기
            바랍니다.
          </P>
          <P>
            ④ 무상으로 지급된 하트는 환불 대상이 아닙니다.
          </P>
        </Section>

        <Section no={12} title="청약철회 및 환불">
          <Note>
            <B>중요.</B> 하트는 App Store 또는 Google Play 인앱결제로 판매되므로, 결제 취소·환불의 <B>실행</B>은 결제하신
            스토어를 통해 이루어집니다. 다만 이는 처리 경로에 관한 사항이며, <B>관련 법령이 회원에게 보장하는 청약철회권을
            제한하지 않습니다.</B>
          </Note>
          <P>
            ① 회원은 하트를 <B>구매한 날부터 7일 이내</B>에 청약철회를 할 수 있습니다.
          </P>
          <Basis>「전자상거래 등에서의 소비자보호에 관한 법률」 제17조 제1항</Basis>
          <P>
            ② 이미 <B>사용한 하트는 청약철회의 대상이 되지 않습니다.</B> 매칭 성사로 차감된 하트는 해당 하트에 대한 서비스
            제공이 완료된 것으로 봅니다. 다만 <B>묶음으로 구매한 하트의 일부만 사용한 경우, 사용하지 않은 하트에
            대하여는 제1항의 기간 내에 청약철회를 할 수 있습니다.</B>
          </P>
          <Basis>같은 법 제17조 제2항 제5호 단서 (가분적 디지털콘텐츠)</Basis>
          <P>③ 청약철회 및 환불 절차는 다음과 같습니다.</P>
          <Ol>
            <li>
              <B>구매 전체에 대한 환불</B>: 결제하신 스토어(App Store 또는 Google Play)의 환불 요청 절차를 통해 스토어
              사업자에게 직접 요청할 수 있습니다. 이 경로가 가장 빠릅니다.
            </li>
            <li>
              <B>일부 사용한 묶음의 미사용분 등 스토어 절차로 처리되지 않는 청약철회</B>: 제19조의 연락처로 회사에 요청하면
              회사가 구매·사용 내역을 확인한 후 처리합니다.
            </li>
          </Ol>
          <P>
            ④ 스토어 또는 회사를 통해 환불이 완료된 경우, 회사는 해당 결제로 지급된 하트를 회수할 수 있습니다. 이미 사용한
            경우에는 보유 하트에서 차감하거나, 보유 하트가 부족한 경우 이용을 제한할 수 있습니다.
          </P>
          <P>
            ⑤ 미성년자가 법정대리인의 동의 없이 결제한 경우 법정대리인은 취소를 요구할 수 있습니다. 다만 서비스는 만 19세
            이상만 이용 가능하며 본인확인을 통해 연령을 확인합니다.
          </P>
          <P>
            <Todo>2건 — ① App Store 결제의 청약철회 의무 주체 (Apple이 merchant of record인 구조에서 회사·Apple 간 의무 배분) ② 하트의 가분적 디지털콘텐츠 해당 여부 (제2항 단서 적용 범위)</Todo>
          </P>
        </Section>

        <Section no={13} title="이용계약의 해지">
          <P>
            ① 회원은 언제든지 앱 내 프로필 화면의 회원 탈퇴 기능을 통해 이용계약을 해지할 수 있습니다. 웹을 통한 삭제 요청은{' '}
            <Link to="/account-deletion" className="text-red-400 hover:text-red-300">계정 및 데이터 삭제</Link> 안내를
            참고해 주세요.
          </P>
          <P>
            ② 해지 시 회원의 개인정보는{' '}
            <Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link>에 따라 처리되며, 보유
            하트는 제11조 제3항에 따라 소멸됩니다.
          </P>
          <P>③ 회사는 제9조에 따라 이용계약을 해지할 수 있습니다.</P>
        </Section>

        <Section no={14} title="회사의 의무">
          <P>
            ① 회사는 관련 법령과 이 약관을 준수하며, 계속적이고 안정적으로 서비스를 제공하기 위해 노력합니다.
          </P>
          <P>
            ② 회사는 회원의 개인정보를 보호하기 위해 안전성 확보에 필요한 기술적·관리적 조치를 취합니다.
          </P>
          <P>
            ③ 회사는 회원이 제기한 의견이나 불만이 정당하다고 인정되는 경우 이를 처리하며, 처리 과정과 결과를 회원에게 전달합니다.
          </P>
        </Section>

        <Section no={15} title="손해배상 및 책임의 제한">
          <P>
            ① 회사는 서비스 제공과 관련하여 회사의 고의 또는 과실로 회원에게 발생한 손해를 배상합니다.
          </P>
          <P>
            ② <B>회사는 회원 간의 만남 및 그 과정에서 발생하는 분쟁이나 손해에 대하여 책임을 지지 않습니다.</B> 서비스는 회원
            간의 만남을 매개하는 수단을 제공할 뿐이며, 회원이 등록한 정보의 진실성이나 회원 간 상호작용의 결과를 보증하지
            않습니다. 다만 회사의 고의 또는 과실이 있는 경우에는 그러하지 않습니다.
          </P>
          <P>
            ③ 회사는 천재지변, 통신 서비스 중단, 회원의 귀책사유 등 회사의 책임 없는 사유로 발생한 손해에 대하여 책임을 지지
            않습니다.
          </P>
          <P>
            ④ 회사는 회원이 서비스를 통해 기대하는 매칭 성사, 관계 형성 등의 결과를 보증하지 않습니다.
          </P>
          <P>
            <Todo>제15조 — 면책 범위가 약관규제법상 무효 조항에 해당하지 않는지 법률 검토 필수</Todo>
          </P>
        </Section>

        <Section no={16} title="지식재산권">
          <P>
            ① 서비스에 대한 저작권 및 지식재산권은 회사에 귀속됩니다. 다만 회원이 등록한 게시물의 저작권은 해당 회원에게
            귀속됩니다.
          </P>
          <P>
            ② 회원은 서비스를 이용하여 얻은 정보를 회사의 사전 승낙 없이 복제·전송·출판·배포·방송하거나 제3자에게 이용하게 할 수
            없습니다.
          </P>
        </Section>

        <Section no={17} title="분쟁의 해결">
          <P>
            ① 회사와 회원은 서비스 이용과 관련하여 발생한 분쟁을 원만하게 해결하기 위해 필요한 모든 노력을 합니다.
          </P>
          <P>
            ② 제1항의 노력에도 분쟁이 해결되지 않는 경우, 양 당사자는 소비자기본법에 따른 소비자분쟁조정위원회 또는
            전자문서·전자거래분쟁조정위원회에 조정을 신청할 수 있습니다.
          </P>
          <P>
            ③ 이 약관은 대한민국 법령에 따라 규율되며, 회사와 회원 간 발생한 분쟁에 관한 소송의 관할법원은{' '}
            <B>「민사소송법」에 따릅니다.</B>
          </P>
          <Note>
            특정 법원을 전속관할로 지정하지 않습니다. 소비자 거래에서 소비자에게 불리한 전속관할 합의는 「약관의 규제에 관한
            법률」에 따라 효력이 제한될 수 있으므로, 민사소송법이 정하는 관할에 따릅니다.
          </Note>
        </Section>

        <Section no={18} title="약관 외 준칙">
          <P>
            이 약관에 명시되지 않은 사항은 「전자상거래 등에서의 소비자보호에 관한 법률」, 「약관의 규제에 관한 법률」,
            「정보통신망 이용촉진 및 정보보호 등에 관한 법률」, 「개인정보 보호법」, 「위치정보의 보호 및 이용 등에 관한 법률」
            등 관계 법령과 회사가 정한 개별 약관·운영정책에 따릅니다.
          </P>
          <P>관련 문서는 다음과 같습니다.</P>
          <Ul>
            <li><Link to="/privacy" className="text-red-400 hover:text-red-300">개인정보처리방침</Link></li>
            <li><Link to="/location-terms" className="text-red-400 hover:text-red-300">위치기반서비스 이용약관</Link></li>
            <li><Link to="/account-deletion" className="text-red-400 hover:text-red-300">계정 및 데이터 삭제 안내</Link></li>
          </Ul>
        </Section>

        <Section no={19} title="회사 정보 및 문의처">
          <Table>
            <thead><tr><Th colSpan={2}>회사 정보</Th></tr></thead>
            <tbody>
              <tr><Td>상호</Td><Td>Cupid</Td></tr>
              <tr><Td>사업자등록번호</Td><Td>438-37-01587</Td></tr>
              <tr><Td>대표자</Td><Td>정지성</Td></tr>
              <tr><Td>주소</Td><Td>서울특별시 송파구 충민로 10, 8층 S16-M117호(문정동, 가든파이브툴)</Td></tr>
              <tr><Td>전화번호</Td><Td><a className="text-red-400 hover:text-red-300" href="tel:010-4027-8182">010-4027-8182</a></Td></tr>
              <tr>
                <Td>전자우편</Td>
                <Td><a className="text-red-400 hover:text-red-300" href="mailto:support@cupidlab.io">support@cupidlab.io</a></Td>
              </tr>
            </tbody>
          </Table>
        </Section>

        <Section title="부칙">
          <P>이 약관은 2026년 10월 1일부터 시행합니다.</P>
        </Section>

    </LegalPage>
  );
}
